import { ButtonPrimary } from "@nova/component-btn"
import { VideoFlight } from "@nova/protobuf"
import {
    selectColorFinderFlights,
    selectVideoFlights,
} from "features/entity-caching/flights/selectors"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useMemo, useState } from "react"
import { LayersRadioButton } from "themes/default-tailwind/layers/LayersRadioButton"
import { LayersSearchBar } from "themes/default-tailwind/layers/LayersSearchBar"
import * as Icons from "@nova/component-icons"
import { updateVideoColorFinderFlight } from "features/pubsub/entityActions/flights/videoColorFinder"
import { selectUserID } from "user/profile/selectors"
import { useColorFinderVideoProcessing } from "../VideoColorFinder/useColorFinderProcessing"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import { getVideoS3Key, selectVideoJobByFlightId } from "plugins/Video/FMV/VideoSlice"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "features/map/control/selectors"
import { selectVideoUploads } from "features/entity-caching/images/video/selectors"
import { get } from "nodash"
import { SidePanelPluginActionsRenamer } from "panels/content/side-panel/plugin-actions/SidePanelPluginActionsRenamer"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import { progressController } from "../VideoColorFinder/preview-files/VideoColorfinderPreviewFiles"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { addUploadTimestamp } from "features/map/layers/slice"
import { selectActiveLayerId } from "panels/content/side-panel/slice.new"

export const ColorfinderVideoSelection = ({
    abortController,
}: {
    abortController: AbortController
}) => {
    const dispatch = useAppDispatch()
    const videoFlights = useAppSelector(selectVideoFlights) as VideoFlight[]
    const workspaceId = useAppSelector(selectActiveWorkspaceId())!
    const projectId = useAppSelector(selectActivatedFireId)!
    const userId = useAppSelector(selectUserID)
    const flightId = useAppSelector(selectActiveLayerId)

    const [searchContents, setSearchContents] = useState("")
    const [selectedVideoId, setSelectedVideoId] = useState("")

    const { processFromLayerCallback } = useColorFinderVideoProcessing((p: number[]) => {
        progressController.setProgresses(flightId!, p)
    })

    const uploads = useAppSelector(selectVideoUploads(selectedVideoId))

    const flights = useAppSelector(selectColorFinderFlights)
    const defaultname = `Colorfinder ${Object.values(flights).length + 1}`

    const videoJob = useAppSelector(selectVideoJobByFlightId(selectedVideoId))
    const metadataPath = get(videoJob, "metadataPath", "")

    const [nameInput, setNameInput] = useState(defaultname)

    useEffectUnsafe(() => {
        if (!nameInput || !flightId) return
        dispatch(updateVideoColorFinderFlight({ id: flightId, updates: { name: nameInput } }))
    }, [nameInput])

    const s3Key = useMemo(() => {
        if (!uploads) return
        const videoUpload = uploads.find((upload) => {
            const name = get(upload, "name", "") as string
            return name.toLowerCase().includes(".mp4")
        })
        return getVideoS3Key({
            organizationID: workspaceId,
            fireID: projectId,
            flightID: selectedVideoId,
            file: videoUpload as unknown as File,
            setID: selectedVideoId,
        })
    }, [uploads, workspaceId, projectId, selectedVideoId])

    const filteredVideoFlights = useMemo(
        () =>
            videoFlights.filter((flight) => {
                if (!searchContents) return true

                const lowerCaseSearch = searchContents.toLowerCase()
                return flight.name.toLowerCase().includes(lowerCaseSearch)
            }),
        [videoFlights, searchContents]
    )
    if (!userId) return null
    return (
        <>
            {/* Show layer rename field only while a method hasn't been selected yet. */}
            <SidePanelPluginActionsRenamer defaultName={defaultname} onRename={setNameInput} />
            <h5 className="text-gray-0 mb-2">Select a Video Layer</h5>
            {videoFlights.length >= 10 && (
                <LayersSearchBar
                    searchContents={searchContents}
                    setSearchContents={setSearchContents}
                />
            )}
            <ScrollableArea className="flex flex-col gap-1 mt-2">
                {filteredVideoFlights.map((flight, idx) => (
                    <LayersRadioButton
                        key={idx}
                        layerId={flight.id}
                        layerName={flight.name}
                        selectedLayerId={selectedVideoId}
                        setSelectedLayerId={setSelectedVideoId}
                        disabled={!flight.job}
                        warning={!flight.job ? "Not Uploaded" : ""}
                    />
                ))}
            </ScrollableArea>
            <ButtonPrimary
                title="Process"
                RightIcon={Icons.ArrowRight}
                className="mt-4 w-full"
                disabled={!selectedVideoId || !s3Key}
                onClick={async () => {
                    if (!s3Key || !flightId) return
                    const videoUrl = await fileManagementApi.getDownloadUrl(s3Key)
                    processFromLayerCallback(
                        videoUrl,
                        flightId,
                        {
                            videoPath: videoJob.videoPath,
                            srtPath: videoJob.srtPath,
                            metadataPath,
                        },
                        abortController
                    )
                    dispatch(
                        updateVideoColorFinderFlight({
                            id: flightId,
                            updates: { referenceVideoId: selectedVideoId },
                        })
                    )
                    dispatch(addUploadTimestamp({ flightId, timestamp: new Date().toISOString() }))
                }}
            />
        </>
    )
}
